import { useState } from 'react';
import { addMailbox, verifyMailbox, type VerifyResult } from '@/api/mailboxes';
import { connectGmail, type GmailTier } from '@/api/oauth';
import { cn } from '@/lib/utils';
import { Loader2, Check, AlertCircle, Mail } from 'lucide-react';

type Step = 'credentials' | 'category' | 'done';

interface MailboxSetupProps {
  onComplete?: () => void;
}

export function MailboxSetup({ onComplete }: MailboxSetupProps) {
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [category, setCategory] = useState<'personal' | 'business'>('personal');
  const [tier, setTier] = useState<GmailTier>('modify');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ email: string } | null>(null);
  const [conn, setConn] = useState<{ running: boolean; result?: VerifyResult } | null>(null);

  const runConnect = async () => {
    setError(null);
    setConn({ running: true });
    try {
      const v = await verifyMailbox(email, password);
      if (!v.ok) { setConn({ running: false, result: v }); return; }
      await addMailbox({ email, password, category, displayName: displayName || undefined });
      setConn(null);
      setResult({ email });
      setStep('done');
      if (onComplete) setTimeout(onComplete, 1200);
    } catch (err: any) {
      setConn({ running: false, result: { ok: false, failedStep: 'auth', error: err.message || 'Could not save the mailbox.' } });
    }
  };
  const handleDiscover = (e: React.FormEvent) => { e.preventDefault(); runConnect(); };

  const handleConnectGmail = () => {
    // Redirect to backend → Google consent → callback → back to app
    connectGmail({ category, displayName: displayName || undefined, tier });
  };

  const TIERS: { id: GmailTier; label: string; desc: string }[] = [
    { id: 'readonly', label: 'Read only', desc: 'B-Mail can read your mail and sort it into views, but never sends, moves or deletes anything.' },
    { id: 'modify', label: 'Read + Send', desc: 'Read, compose, reply, and send. Delete moves to trash (nothing is permanently erased).' },
    { id: 'full', label: 'Full access', desc: 'Everything, including permanent delete. Power users only.' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {['Credentials', 'Category', 'Done'].map((label, idx) => (
          <div key={label} className="flex flex-1 items-center">
            <div
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
                step === ['credentials', 'category', 'done'][idx]
                  ? 'bg-bmail-goldfill text-bmail-on-accent'
                  : idx < ['credentials', 'category', 'done'].indexOf(step)
                  ? 'bg-green-500 text-bmail-text'
                  : 'bg-bmail-border text-bmail-text-secondary'
              )}
            >
              {idx + 1}
            </div>
            <span className="ml-2 text-xs font-medium text-bmail-text hidden sm:inline">{label}</span>
            {idx < 2 && <div className="mx-2 h-px flex-1 bg-bmail-border" />}
          </div>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-bmail-error/10 px-4 py-3 text-sm text-bmail-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {step === 'credentials' && (
        <div className="space-y-4">
          {/* Category — chosen up front so it applies to Gmail OAuth too */}
          <div>
            <label className="mb-2 block text-sm font-medium text-bmail-text">How do you use this address?</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCategory('personal')}
                className={cn(
                  'rounded-xl border px-4 py-3 text-center font-semibold transition-colors',
                  category === 'personal'
                    ? 'border-bmail-gold bg-bmail-surface-hover text-bmail-gold'
                    : 'border-bmail-border text-bmail-text hover:bg-bmail-surface-hover'
                )}
              >
                👤 Personal
              </button>
              <button
                type="button"
                onClick={() => setCategory('business')}
                className={cn(
                  'rounded-xl border px-4 py-3 text-center font-semibold transition-colors',
                  category === 'business'
                    ? 'border-bmail-gold bg-bmail-surface-hover text-bmail-gold'
                    : 'border-bmail-border text-bmail-text hover:bg-bmail-surface-hover'
                )}
              >
                💼 Business
              </button>
            </div>
          </div>

          {/* Gmail OAuth — one-click connect */}
          <div className="rounded-xl border border-bmail-border bg-bmail-navy p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-lg font-bold">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10 10-4.477 10-10z" fill="#4285F4"/>
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M3.98 9.09c-.48.78-.76 1.69-.76 2.91s.28 2.13.76 2.91l3.57-2.77c-.48-.78-.76-1.69-.76-2.91s.28-2.13.76-2.91L3.98 9.09z" fill="#FBBC05"/>
                  <path d="M12 22c2.7 0 4.96-.89 6.61-2.42l-3.57-2.77c-.99.66-2.26 1.06-3.04 1.06-2.06 0-3.81-1.39-4.44-3.25l-3.57 2.77C3.64 20.45 7.13 22 12 22z" fill="#34A853"/>
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                </svg>
              </div>
              <div>
                <div className="font-semibold text-bmail-text">Connect Gmail</div>
                <div className="text-xs text-bmail-text-secondary">One-click OAuth — no app password needed</div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleConnectGmail}
              className="w-full rounded-lg bg-white px-4 py-2.5 font-semibold text-gray-900 hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <Mail size={18} />
              Continue with Google
            </button>

            {/* Access tier — chosen before consent so Google's screen shows
                exactly what's being granted (2026-08-24) */}
            <div className="mt-3">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-bmail-text-secondary">
                Access level for Gmail
              </label>
              <div className="space-y-1.5">
                {TIERS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTier(t.id)}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2 text-left transition-colors',
                      tier === t.id
                        ? 'border-bmail-goldfill bg-bmail-goldfill/10'
                        : 'border-bmail-border hover:bg-bmail-surface-hover'
                    )}
                  >
                    <div className="text-sm font-semibold text-bmail-text">{t.label}</div>
                    <div className="text-xs text-bmail-text-secondary">{t.desc}</div>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-bmail-text-secondary">
                Confirmed by Google on the consent screen. Changeable later via Reconnect.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-bmail-border" />
            <span className="text-xs text-bmail-text-secondary">or connect manually</span>
            <div className="h-px flex-1 bg-bmail-border" />
          </div>

          {/* Manual IMAP form */}
          <form onSubmit={(e) => { e.preventDefault(); setStep('category'); }} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-bmail-text">Email address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-bmail-border bg-bmail-navy px-4 py-2.5 text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-bmail-text">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-bmail-border bg-bmail-navy px-4 py-2.5 text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-bmail-goldfill px-4 py-2.5 font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover transition-colors"
            >
              Continue
            </button>
          </form>
        </div>
      )}

      {step === 'category' && (
        <form onSubmit={handleDiscover} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-bmail-text">Display name (optional)</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded-lg border border-bmail-border bg-bmail-navy px-4 py-2.5 text-bmail-text placeholder-slate-500 focus:border-bmail-gold focus:outline-none"
              placeholder="Work Email"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep('credentials')}
              className="flex-1 rounded-lg border border-bmail-border px-4 py-2.5 font-semibold text-bmail-text hover:bg-bmail-surface-hover"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={!!conn?.running || busy}
              className="flex-1 rounded-lg bg-bmail-goldfill px-4 py-2.5 font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover disabled:opacity-50 transition-colors"
            >
              Connect mailbox
            </button>
          </div>
        </form>
      )}

      {step === 'done' && result && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20 text-bmail-success">
            <Check size={32} />
          </div>
          <h3 className="text-xl font-bold text-bmail-text">Mailbox connected</h3>
          <p className="mt-1 text-bmail-text">{result.email}</p>
        </div>
      )}

      {conn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl border border-bmail-border bg-bmail-surface p-5">
            <h3 className="mb-3 font-semibold text-bmail-text">Connecting to {email}</h3>
            <div className="space-y-2">
              {[
                { key: 'discover', label: 'Finding your mail server' },
                { key: 'connect', label: 'Connecting securely' },
                { key: 'auth', label: 'Signing in' }
              ].map((s) => {
                const order = ['discover', 'connect', 'auth'];
                const f = conn.result?.failedStep;
                let status: 'run' | 'ok' | 'fail' | 'idle';
                if (conn.running) status = 'run';
                else if (!f) status = 'ok';
                else status = order.indexOf(s.key) < order.indexOf(f) ? 'ok' : order.indexOf(s.key) === order.indexOf(f) ? 'fail' : 'idle';
                return (
                  <div key={s.key} className="flex items-center gap-2 text-sm">
                    {status === 'run' && <Loader2 size={16} className="animate-spin text-bmail-text-secondary" />}
                    {status === 'ok' && <Check size={16} className="text-bmail-success" />}
                    {status === 'fail' && <AlertCircle size={16} className="text-bmail-error" />}
                    {status === 'idle' && <div className="h-4 w-4 rounded-full border border-bmail-border" />}
                    <span className={status === 'fail' ? 'text-bmail-error' : status === 'idle' ? 'text-bmail-text-secondary' : 'text-bmail-text'}>{s.label}</span>
                  </div>
                );
              })}
            </div>
            {conn.result && !conn.result.ok && (
              <>
                <p className="mt-3 rounded-lg bg-bmail-error/10 px-3 py-2 text-sm text-bmail-error">{conn.result.error}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setConn(null)} className="flex-1 rounded-lg border border-bmail-border px-4 py-2 text-sm font-semibold text-bmail-text hover:bg-bmail-surface-hover">Back</button>
                  <button onClick={runConnect} className="flex-1 rounded-lg bg-bmail-goldfill px-4 py-2 text-sm font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover">Try again</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}